import * as queries from './queries';
import app from '../../app';
import {printDSSDebugInfo} from "../../../helper/DSSDebug";
import {
  getGraphQLErrorForPathAndCodes,
  RUNTIME_MISSING_DSS_DATA_EXCEPTION,
  RUNTIME_ALGORITHM_DID_NOT_RUN
} from "../error/graphql-errors";
import {simpleClone} from "../../../helper/objects";
import gql from 'graphql-tag';
import moment from 'moment';

export async function replayAlgorithm({dispatch, commit, rootGetters, getters, state}, params) {
  const {patientId, algorithmUuid} = params,
      query = queries.createDSSMedicationSchedulesQuery(true, true),
      variables = {
        patientId,
        lastRunAlgorithmId: algorithmUuid,
        skipAlgorithm: "0",
        forceRefresh: "0"
      }
  ;

  const {data, errors} = await app.apolloClient.mutate({
    mutation: query,
    variables: variables,
  });

  if (data && !errors) {
    const {DSSMedicationSchedules} = data;

    if (DSSMedicationSchedules) {
      printDSSDebugInfo(
          DSSMedicationSchedules[0].algorithm,
          patientId,
          rootGetters.getServerInfo("dssVersion"),
      )
    }

  } else if (errors.length) {
    errors.forEach((error) => {
      dispatch('error/submit', error, {root: true});
    });
  }
}

export async function getDSSMedicationSchedules({ dispatch, commit, rootGetters, getters, state }, parameters) {
  const patientId = rootGetters['patientSummary/id'];

  // Just fetch medication schedules without running algorithm
  const skipAlgorithm = parameters && parameters.skipAlgorithm;

  // Always refresh the algorithm instead of reusing last one
  const forceRefresh = parameters && parameters.forceRefresh;
  // Show the popup window if the DSS algorithm did not run
  const missingDSSFeedback = parameters && typeof parameters.missingDSSFeedback !== "undefined" ? parameters.missingDSSFeedback : true;

  // Date since when to check if prescription was changed by cardiologist ( week before )
  const dateLastChanged = moment().subtract(1, 'weeks').format('YYYY-MM-DD HH:mm:ss');

  try {
    const
        { debugDSSAlgorithm } = getters,
        query = queries.createDSSMedicationSchedulesQuery(false, debugDSSAlgorithm),
        variables = {
          patientId,
          skipAlgorithm : skipAlgorithm ? "1" : "0",
          forceRefresh : forceRefresh ? "1" : "0",
          sinceDate: dateLastChanged
        }
    ;

    const { data, errors } = await app.apolloClient.mutate({
      mutation: query,
      variables: variables,
    });

    if(data && !errors) {
      const { DSSMedicationSchedules } = data;

      if (DSSMedicationSchedules) {
        if (debugDSSAlgorithm){
          // console.clear();

          if (!skipAlgorithm){
            printDSSDebugInfo(
                DSSMedicationSchedules[0].algorithm,
                patientId,
                rootGetters.getServerInfo("dssVersion"),
            )
          }
        }

        commit('setDSSMedicationSchedules', DSSMedicationSchedules);

        // on initial DSS load save last audit trails seperately to not be effected by any change
        // ( indicates if a change was made by the patient from last time DSS was open )
        if (getters.DSSFirstLoad.DSS) {
          commit('setDSSFirstLoad', { ...getters.DSSFirstLoad, DSS: false });
          commit('setDSSLastAuditTrails', DSSMedicationSchedules[0].medicationSchedules.map(item => {
            return {
              id: item.id,
              lastAuditTrail: item.lastAuditTrail
            }
          }));
        }
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (graphQLError) {
    const algoDidNotRunExceptions = getGraphQLErrorForPathAndCodes(graphQLError, 'patientId',
        [RUNTIME_MISSING_DSS_DATA_EXCEPTION, RUNTIME_ALGORITHM_DID_NOT_RUN]
    );

    if (algoDidNotRunExceptions.length){
      // get DSS schedules again, but this time without algorithm so you can still see and edit the medication
      await dispatch('getDSSMedicationSchedules', {
        skipAlgorithm : true,
        missingDSSFeedback : false,
        sinceDate: dateLastChanged
      });

      // Get message with missing data information to add to popup
      if (missingDSSFeedback){
        state.missingDSSPopupMessage = algoDidNotRunExceptions[0].message;
      }
    }else{
      dispatch('error/submit', graphQLError, { root: true });
    }
  }
}

export async function submitDSSMedicationSchedule({
  dispatch,
  commit,
  rootGetters
}, payload) {
  const patientId = rootGetters['patientSummary/id'];
  const { medication } = payload;

  const dosages = medication.dosages ? simpleClone(medication.dosages) : [];
  dosages.forEach(dosage => {
    if (dosage.unit !== 'string' && dosage?.unit?.unit){
      dosage.unit = dosage.unit.unit;
    }
    dosage.value = dosage.value !== null ? parseFloat(dosage.value) : null;
  })

  try {
    const { data, errors } = await app.apolloMobileClient.mutate({
      mutation: queries.createUpdateMyMedicationSchedule,
      variables: {
        ...(medication.id && { id: medication.id }),
        patientId,
        name: medication.name,
        administration: medication.administration,
        dosages: dosages,
        notes: medication.notes,
        morningAmount: medication.morningAmount === null ? medication.morningAmount : parseFloat(medication.morningAmount),
        noonAmount: medication.noonAmount === null ? medication.noonAmount : parseFloat(medication.noonAmount),
        afternoonAmount: medication.afternoonAmount === null ? medication.afternoonAmount : parseFloat(medication.afternoonAmount),
        eveningAmount: medication.eveningAmount === null ? medication.eveningAmount : parseFloat(medication.eveningAmount),
        schedule: medication.schedule,
        classes: medication.classes,
      },
    });

    if (data && !errors) {
      const { createUpdateMyMedicationSchedule } = data;

      // Update new dose and rate ids for newly added medication class rows.
      if (createUpdateMyMedicationSchedule?.dosage?.doseAndRate){
        createUpdateMyMedicationSchedule.dosage.doseAndRate.forEach((dar, index) => {
          medication.dosages[index].id = dar.id;
        })
      }

      if (createUpdateMyMedicationSchedule) {
        await dispatch('getDSSMedicationSchedules', {missingDSSFeedback : false});
        await dispatch('getDSSMedicationScheduleAuditTrails');
        return true;
      }

      return false;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });

      return false;
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });

    return false;
  }
}

export function addMedicationSchedule({ commit }) {
  commit('addMedicationSchedule');
}

export async function removeMedicationSchedule({ dispatch, commit }, payload) {
  const { id, index } = payload;

  if (!id) {
    commit('removeMedicationSchedule', index);
    return true;
  }

  try {
    const { data, errors } = await app.apolloMobileClient.mutate({
      mutation: queries.deleteMyMedicationSchedule,
      variables: { id },
    });

    if (data && !errors) {
      const { deleteMyMedicationSchedule } = data;

      if (deleteMyMedicationSchedule === id) {
        await dispatch('getDSSMedicationSchedules', {missingDSSFeedback : false});

        return true;
      }

      return false;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });

      return false;
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });

    return false;
  }
}

export async function getMedicationClasses({ dispatch, commit }) {
  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.getMedicationClasses,
    });

    if(data && !errors) {
      const { medicationClasses } = data;

      if (medicationClasses && medicationClasses.length) {
        commit('setMedicaitonClasses', medicationClasses);
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getPatientQuestionnaires({ dispatch, commit, rootGetters }) {
  const patientId = rootGetters['patientSummary/id'];

  try {
    const { data, errors } = await app.apolloMobileClient.query({
      query: queries.getPatientQuestionnaires,
      variables: { patientId },
    });

    if(data && !errors) {
      const { myQuestionnaireForms } = data;

      if (myQuestionnaireForms && myQuestionnaireForms.length) {
        commit('setPatientQuestionnaires', myQuestionnaireForms);
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function getDSSMedicationScheduleAuditTrails({
  dispatch, commit, rootGetters
}) {
  const patientId = rootGetters['patientSummary/id'];

  try {
    const { data, errors } = await app.apolloClient.query({
      query: queries.getAuditTrails,
      variables: { patientId },
    });

    if(data && !errors) {
      const { medicationScheduleAuditTrails } = data;

      if (medicationScheduleAuditTrails && medicationScheduleAuditTrails.edges) {
        commit('setAuditTrails', medicationScheduleAuditTrails);
      }
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
  }
}

export async function reset({ commit }) {
  commit('reset');
}

export const setPrescriptionRecentlyUpdatedWarning = ({commit, getters}, updated) => {
  let recentlyUpdated = updated;
  if (updated === undefined) {
    recentlyUpdated = getters.medicationSchedules.some(med => med?.wasChangedSinceDate)
  }
  commit('setPrescriptionRecentlyUpdatedWarning', recentlyUpdated)
}

export const setSchedulesToBeDeleted = ({commit, getters}, {add, reset}) => {
  // increase count if new prescription is in queue to be deleted
  // decrease after deleted or canceled
  let count = getters.schedulesToBeDeleted;
  if (add && !reset) {
    count++;
  } else if (count > 0 && !reset) {
    count--;
  } else {
    count = 0;
  }
  commit('setSchedulesToBeDeleted', count)
}

export async function deleteMultipleTitrationSchemes({ dispatch }, schemeIds) {
  if (!schemeIds || !schemeIds.length) return;

  const variableString = schemeIds.map((id, i) => {
    return `$id_${i}: String!`
  }).join(', ');

  const variables = schemeIds.reduce((obj, curr, i) => {
    return { ...obj, [`id_${i}`]: curr }
  }, {})

  const queryString = schemeIds.map((id, i) => {
    return `deleteTitrationScheme_${i}: deleteTitrationScheme(id: $id_${i})`
  }).join(' ');

  try {
    const { data, errors } = await app.apolloClient.query({
      query: gql`
        mutation deleteTitrationSchemes(${variableString}) {
          ${queryString}
        }
      `, variables: variables,
    });

    if (data && !errors) {
      return data;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export async function rejectMultipleRecommendations({ dispatch }, rejections) {
  if (!rejections || !rejections.length) return;

  const variableString = rejections.map((item, i) => {
    return `
      $medicationRecommendationId_${i}: String!,
      $types_${i}: [String!],
      $other_${i}: String
    `
  }).join(', ');

  const variables = rejections.reduce((obj, curr, i) => {
    return {
      ...obj,
      [`medicationRecommendationId_${i}`]: curr.medicationRecommendationId,
      [`types_${i}`]: curr.types,
      [`other_${i}`]: curr.other
    }
  }, {})

  const queryString = rejections.map((item, i) => {
    return `rejectDSSRecommendation_${i}: rejectDSSRecommendation(
      medicationRecommendationId : $medicationRecommendationId_${i}
      types : $types_${i}
      other : $other_${i}
    ) {
      id
    }`
  }).join(' ');

  try {
    const { data, errors } = await app.apolloClient.query({
      query: gql`
        mutation rejectDSSRecommendations(${variableString}) {
          ${queryString}
        }
      `, variables: variables,
    });

    if (data && !errors) {
      return data;
    } else if (errors.length) {
      errors.forEach((error) => {
        dispatch('error/submit', error, { root: true });
      });
    }
  } catch (error) {
    dispatch('error/submit', error, { root: true });
    return false;
  }
}

export const setDSSFirstLoad = ({commit}, DSSFirstLoad) => {
  commit('setDSSFirstLoad', DSSFirstLoad);
}

export const setDSSLastAuditTrails = ({commit}, DSSLastAuditTrails) => {
  commit('setDSSLastAuditTrails', DSSLastAuditTrails);
}

export const resetDSSLastAuditTrails = ({commit}) => {
  commit('setDSSLastAuditTrails', []);
  commit('setDSSFirstLoad', { DSS: true, screens: [] });
  localStorage.removeItem('DSSLastAuditTrails');
  localStorage.removeItem('DSSFirstLoad');
}